<template>
  <div class="stt-wrapper">
    <div class="content-area">
      <div class="page-header">
        <h1 class="page-title">
          STT
        </h1>
      </div>

      <div class="main-content main-content--split-row">
        <div class="left-panel">
          <div class="form-content">
            <div class="form-group">
              <label class="form-label required">
                Audio file
                <span class="required-asterisk">*</span>
              </label>
              <v-file-input
                v-model="formData.file"
                placeholder="Select audio file"
                outlined
                accept="audio/*"
                :error-messages="errors.file"
                hide-details="auto"
                class="form-input mank-input stt-file-input"
                dense
                @change="onFileChange">
                <template v-slot:append>
                  <Icon name="upload" variant="solid" color="muted" size="20"/>
                </template>
              </v-file-input>
              <p class="helper-text mt-1">
                Supported formats: audio/* (e.g. wav, mp3)
              </p>
            </div>
            <div class="form-actions">
              <v-btn
                color="primary"
                class="mank-btn"
                elevation="0"
                :loading="transcribing"
                :disabled="!canTranscribe"
                @click="transcribe">
                <v-icon left size="20">
                  mdi-microphone
                </v-icon>
                Transcribe
              </v-btn>
            </div>
          </div>
        </div>

        <div class="right-panel">
          <div class="form-group output-panel-group">
            <h3 class="form-label output-panel-title ds-panel-title">
              Output
            </h3>
            <div class="output-section">
              <div v-if="!transcriptionResult" class="empty-output">
                <v-icon size="48" color="grey-lighten-1">
                  mdi-text-box-outline
                </v-icon>
                <p class="text-body-2 text--secondary mt-2">
                  Upload an audio file and transcribe it.
                </p>
              </div>
              <div v-else class="transcription-result">
                <pre class="transcription-text">{{ transcriptionResult }}</pre>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Icon from '../components/Icon.vue';
import SttService from '../services/SttService';
import AnalyticsService from '../services/AnalyticsService';
import { EVENTS } from '../constants/analyticsEvents';

export default {
  name: 'STT',
  components: { Icon },
  data() {
    return {
      formData: { file: null },
      errors: { file: [] },
      transcribing: false,
      transcriptionResult: null,
    };
  },
  computed: {
    canTranscribe() {
      return !!this.formData.file;
    },
  },
  methods: {
    onFileChange() {
      this.errors.file = [];
    },
    /** API `data` alanını (segments, success, error, …) olduğu gibi gösterir. */
    formatSttDataPayload(data) {
      if (data == null) return '';
      if (typeof data === 'string') return data;
      try {
        return JSON.stringify(data, null, 2);
      } catch {
        return String(data);
      }
    },
    async transcribe() {
      this.errors.file = [];
      const raw = this.formData.file;
      const file = Array.isArray(raw) ? raw[0] : raw;
      if (!file || !(file instanceof File)) {
        this.errors.file = ['Audio file is required'];
        return;
      }
      this.transcribing = true;
      this.transcriptionResult = null;
      try {
        const res = await SttService.transcribe(file);
        if (res && res.success && res.data != null) {
          this.transcriptionResult = this.formatSttDataPayload(res.data);
          AnalyticsService.track(EVENTS.STT_TEST_RUN, { success: true });
          this.$store.dispatch('ui/showNotification', {
            type: 'success',
            title: 'Success',
            message: 'Audio transcribed.',
          });
        } else {
          this.transcriptionResult = res?.message || 'No result received.';
          AnalyticsService.track(EVENTS.STT_TEST_RUN, { success: false });
          this.$store.dispatch('ui/showNotification', {
            type: 'warning',
            title: 'Warning',
            message: res?.message || 'Transcription returned empty.',
          });
        }
      } catch (e) {
        const msg = e.response?.data?.message || e.message || 'Transcription failed.';
        AnalyticsService.track(EVENTS.STT_TEST_RUN, { success: false });
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Error',
          message: msg,
        });
        this.transcriptionResult = null;
      } finally {
        this.transcribing = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/colors.scss';
@import '@/styles/variables.scss';

.main-content {
  align-items: flex-start;
  gap: 24px;
  padding: 24px;
  overflow: hidden;
}

.left-panel {
  flex: 0 0 420px;
  width: 100%;
  max-width: 420px;
  overflow-y: auto;
}

.form-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
}

.form-content :deep(.stt-select),
.form-content :deep(.stt-file-input),
.form-content :deep(.form-input),
.form-content :deep(.mank-input) {
  width: 100%;
}

/* STT file input: match mank-input / TTS select look (same height, background, no border) */
.form-content :deep(.stt-file-input) {
  background: $light-surface-1;
  border-radius: 8px;
  .v-input__control {
    position: relative;
  }
  .v-input__slot {
    min-height: 32px;
    height: 32px;
    padding: 6px 40px 6px 12px !important;
    align-items: center;
  }
  .v-input__append-inner {
    display: none;
  }
  .v-input__append-outer {
    position: absolute !important;
    right: 12px !important;
    top: 0 !important;
    bottom: 0 !important;
    margin: 0 !important;
    display: flex !important;
    align-items: center !important;
    pointer-events: none;
  }
  &.v-input--is-focused .v-input__slot fieldset,
  &.v-text-field--outlined fieldset,
  .v-text-field__slot fieldset {
    border: none !important;
  }
  fieldset {
    border: none !important;
  }
  .v-select__selection,
  .v-text-field__slot input,
  .v-file-input__text {
    color: $light-foreground-1;
  }
  .v-file-input__text {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.form-group {
  .form-label {
    display: block;
    font-size: $body-3-size;
    font-weight: 600;
    color: $light-foreground-1;
    margin-bottom: 8px;
    &.required .required-asterisk {
      color: $light-primary;
    }
  }
  .helper-text {
    font-size: $caption-1-size;
    color: $light-foreground-4;
  }
  .token-warning {
    color: $light-warning;
  }
}

.form-actions {
  margin-top: 8px;
}

.right-panel {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.output-panel-title {
  margin-top: 0;
}

.output-section {
  background: $light-surface-1;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid $light-border;
}

.empty-output {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  color: $light-foreground-4;
}

.transcription-result {
  margin-top: 8px;
}

.transcription-text {
  margin: 0;
  font-family: ui-monospace, 'Cascadia Code', 'SF Mono', Menlo, monospace;
  font-size: $body-3-size;
  line-height: 1.6;
  color: $light-foreground-1;
  white-space: pre-wrap;
  word-break: break-word;
}

.form-content :deep(.v-input) {
  flex: 1 1 auto;
  min-width: 0;
}
</style>
